import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  CatalogueView,
  ConfigModule,
  getCustomerInvite,
  useCheckoutOrdersMutation,
  useGetOrderDetailsMutation,
  useGetConfigDetailsMutation,
  useRemoveOrderPromotionMutation,
  getIsSimulation,
  dispatch,
  updateCurrentTooltipIndex,
  getShippingAddress,
  getCheckoutNotes,
  ShippingAddress,
  useContinueChatMutation,
  useCancelOrderMutation,
  useGenerateRazorpayLinkMutation,
  updateIsSideDrawerOpen,
  useGetOrderLevelSchemesMutation,
  OrderLevelScheme,
  useEditOrderMutation,
  useGetCustomerReportMutation,
} from '@zonofi/zono-money-store';
import {
  OrderData,
  OrderDataLine,
  OrderStatusEnum,
  WEBROUTES,
  extractDataFromCart,
  extractDataFromOrder,
  sendWAEventToParent,
  useAppSelector,
  useCart,
} from '@zonofi/zono-money-helpers';
import { isEmpty } from 'lodash';
import {
  getBackToWhatsappLink,
  onClickBackToWhatsapp,
} from '@zonofi/zono-money-whatsapp-stack';
import _ from 'lodash';
import { getFormattedAmount } from '@zonofi/common';

export const useOrderDetailsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [promotionApplied, setPromotionApplied] = useState(false);

  const isCart = location.pathname?.includes('cart');
  const fromScreen = searchParams?.get('from');
  const hidePrice = searchParams?.get('hp');

  const orderId_ = searchParams?.get('orderId');
  const customerId = searchParams?.get('inviteId');

  const campaignType = searchParams?.get('campaignType');
  const hideGoBack = fromScreen === 'reorder';
  const orderId = Number(params?.orderId) ? params?.orderId : orderId_;

  const [fetchSummary, summaryResponse] = useGetCustomerReportMutation();

  const inviteDetails = useAppSelector(getCustomerInvite);
  const isSimulation = useAppSelector(getIsSimulation);
  const shippingAddress = useAppSelector(getShippingAddress);
  const checkoutNotes = useAppSelector(getCheckoutNotes);
  const { seller, inviteId, waConfig, buyer } = inviteDetails;

  const [fetchOrder, fetchOrderResponse] = useGetOrderDetailsMutation();
  const [fetchOrderAfterEdit, fetchOrderAfterEditResponse] =
    useGetOrderDetailsMutation();
  const [getConfigAPI, getConfigAPIResponse] = useGetConfigDetailsMutation();
  const [getOrderConfigApi, getOrderConfigAPIResponse] =
    useGetConfigDetailsMutation();
  const [checkoutAPI, checkoutAPIResponse] = useCheckoutOrdersMutation();
  const [continueChatAPI, continueChatAPIResponse] = useContinueChatMutation();
  const [cancelOrderAPI, cancelOrderAPIResponse] = useCancelOrderMutation();
  const [removeOrderPromotion, removeOrderPromotionResponse] =
    useRemoveOrderPromotionMutation();
  const [getOrderLevelSchemesAPI] = useGetOrderLevelSchemesMutation();
  const [generateRazorpayLinkAPI, generateRazorpayLinkAPIResponse] =
    useGenerateRazorpayLinkMutation();

  const [error, setError] = useState({
    show: false,
    msg: '',
  });
  const [showToastError, setShowToastError] = useState({
    show: false,
    msg: '',
  });
  const [showToastSuccess, setShowToastSuccess] = useState({
    show: false,
    msg: '',
  });
  const [moqError, setMoqError] = useState({ isError: false, quantity: 0 });
  const [orderDetails, setOrderDetails] = useState<OrderData>();
  const [selectedSection, setSelectedSection] = useState<number>(0);
  const [showPromotionsModal, setShowPromotionsModal] = useState(false);
  const [promotionSchemes, setPromotionSchemes] = useState<OrderLevelScheme[]>(
    []
  );
  const [selectedBenefit, setSelectedBenefit] = useState<OrderLevelScheme>();
  const lastPromotionSyncOrderIdRef = useRef<number | undefined>(undefined);
  const isChangingBenefitRef = useRef(false);
  const [primaryBtnText, setPrimaryBtnText] = useState('');
  const [showTnCModal, setShowTnCModal] = useState(false);

  const hasAvailablePromotions = promotionSchemes.length > 0;
  const {
    callCartAPI,
    cartAPIResponse,
    productsInCart,
    setProductsInCart,
    cartSummaryAPIResponse,
    onIncrement: onCartIncrement,
    onDecrement: onCartDecrement,
    onDelete,
    onChangeQuantity: onCartChangeQuantity,
    onApplyPromotion: applyPromotionOnOrder,
    editOrderResponse,
    deleteLinesAPIResponse,
  } = useCart();

  const isOutOfStockLine = (line?: OrderDataLine) =>
    Number(line?.stock ?? 1) < 1 &&
    getConfigAPIResponse?.data?.config?.displayOutOfStock === true;

  const isShowOutstandingAmountAndCreditLimit =
    getOrderConfigAPIResponse?.data?.config
      ?.showCustomerOutstandingAndCreditLimitInCart;

  const onIncrement = (
    selectedLine: OrderDataLine,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    if (isOutOfStockLine(selectedLine)) {
      setShowToastError({
        show: true,
        msg: 'Out of stock item cannot be added.',
      });
      return;
    }

    onCartIncrement(selectedLine, sellerWorkspaceId, customerId);
  };

  const isDraft = useMemo(
    () =>
      fetchOrderResponse?.data?.status === OrderStatusEnum.ADDING_ITEMS ||
      fetchOrderResponse?.data?.status === OrderStatusEnum.DRAFT,
    [fetchOrderResponse]
  );

  const onChangeQuantity = (
    selectedLine: OrderDataLine,
    qty: number,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    const currentQty = Number(selectedLine?.quantity ?? 0);
    if (isOutOfStockLine(selectedLine) && qty > currentQty) {
      setShowToastError({
        show: true,
        msg: 'Out of stock item cannot be added.',
      });
      return;
    }

    onCartChangeQuantity(selectedLine, qty, sellerWorkspaceId, customerId);
  };

  const onDecrement = (
    selectedLine: OrderDataLine,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    onCartDecrement(selectedLine, sellerWorkspaceId, customerId);
  };

  useEffect(() => {
    fetchSummary({
      params: {
        sellerWorkspaceId: seller?.workspaceId,
      },
      body: {
        customerIds: [customerId ?? inviteId ?? ''],
        sellerworkspaceid: seller?.workspaceId,
      },
    });
  }, []);

  const isPoFile = useMemo(() => {
    return isNaN(Number(orderId));
  }, [orderId]);

  const includePaymentLink = useMemo(() => {
    if (isCart && cartAPIResponse?.data?.includePaymentLink) {
      return cartAPIResponse?.data?.includePaymentLink;
    } else {
      return fetchOrderResponse?.data?.includePaymentLink ?? false;
    }
  }, [orderDetails, cartAPIResponse, fetchOrderResponse]);

  const showAdvancePayment = useMemo(() => {
    const status =
      fetchOrderResponse?.data?.status ||
      cartAPIResponse?.data?.files?.[0]?.status ||
      orderDetails?.status;

    if (
      ![
        OrderStatusEnum.ADDING_ITEMS,
        OrderStatusEnum.DRAFT,
        OrderStatusEnum.SUBMITTEDBYCUSTOMER,
        OrderStatusEnum.SUBMITTED_BY_CUSTOMER,
        OrderStatusEnum.SUBMITTED,
        OrderStatusEnum.CREATED,
        OrderStatusEnum.ORDER_CREATED,
      ].includes(status as any)
    ) {
      return false;
    }
    if (isCart && Number(cartAPIResponse?.data?.advancePayment)) {
      return true;
    } else if (Number(fetchOrderResponse?.data?.advancePayment)) {
      return true;
    } else {
      return false;
    }
  }, [orderDetails, cartAPIResponse, isCart, fetchOrderResponse]);

  const advanceAmount = useMemo(() => {
    if (
      isCart &&
      (Number(cartAPIResponse?.data?.advancePayment) ||
        Number(cartAPIResponse?.data?.advancePaymentAmount))
    ) {
      return getFormattedAmount(
        cartAPIResponse?.data?.advancePaymentType === 'percentage'
          ? Number(cartAPIResponse?.data?.advancePaymentAmount ?? '0')
          : Number(cartAPIResponse?.data?.advancePayment || '0')
      );
    } else if (
      Number(fetchOrderResponse?.data?.advancePayment) ||
      Number(fetchOrderResponse?.data?.advancePaymentAmount)
    ) {
      return getFormattedAmount(
        fetchOrderResponse?.data?.advancePaymentType === 'percentage'
          ? Number(fetchOrderResponse?.data?.advancePaymentAmount ?? '0')
          : Number(fetchOrderResponse?.data?.advancePayment ?? '0')
      );
    } else {
      return '';
    }
  }, [orderDetails, cartAPIResponse, isCart, fetchOrderResponse]);

  const showImage = useMemo(() => {
    if (getConfigAPIResponse?.isSuccess && getConfigAPIResponse?.data) {
      if (
        getConfigAPIResponse?.data?.config?.catalogueView &&
        getConfigAPIResponse?.data?.config?.catalogueView !==
          CatalogueView.ListView
      ) {
        return true;
      }
    }
    return false;
  }, [getConfigAPIResponse]);

  const isGettingConfig = useMemo(() => {
    return getConfigAPIResponse?.isLoading;
  }, [getConfigAPIResponse]);

  const showPrice = useMemo(() => {
    if (hidePrice === 'true') {
      return false;
    }
    if (getConfigAPIResponse?.isSuccess && getConfigAPIResponse?.data) {
      if (
        getConfigAPIResponse?.data?.config?.displayCataloguePrices &&
        getConfigAPIResponse?.data?.config?.displayCataloguePrices !== 'false'
      ) {
        return true;
      }
    }
    return false;
  }, [getConfigAPIResponse, hidePrice]);
  const showOutOfStock = useMemo(() => {
    return getConfigAPIResponse?.data?.config?.displayOutOfStock === true
      ? true
      : false;
  }, [getConfigAPIResponse?.data?.config]);

  const isLoading = useMemo(() => {
    return fetchOrderResponse?.isLoading;
  }, [fetchOrderResponse, getConfigAPIResponse]);

  const isSubmitting = useMemo(() => {
    return checkoutAPIResponse?.isLoading || continueChatAPIResponse?.isLoading;
  }, [checkoutAPIResponse, continueChatAPIResponse]);

  const showOrderEdit = useMemo(() => {
    if (
      orderDetails?.status === OrderStatusEnum.ADDING_ITEMS ||
      orderDetails?.status === OrderStatusEnum.ORDER_CREATED ||
      orderDetails?.status === OrderStatusEnum.MAPPING_FAILED ||
      orderDetails?.status === OrderStatusEnum.PARSING_FAILED ||
      orderDetails?.status === OrderStatusEnum.DRAFT
    ) {
      return true;
    }
    return (
      orderDetails?.status?.toLowerCase()?.includes('submitted') &&
      !['C', 'Customer'].includes(orderDetails?.userType)
    );
  }, [orderDetails]);

  const showFooter = useMemo(() => {
    return (
      showOrderEdit ||
      (includePaymentLink &&
        ([OrderStatusEnum.APPROVED, OrderStatusEnum.BILLED].includes(
          orderDetails?.status as any
        ) ||
          orderDetails?.status?.toLowerCase()?.includes('submitted')))
    );
  }, [showOrderEdit, orderDetails]);

  const showCancelBtn = useMemo(() => {
    return (
      orderDetails?.status?.toLowerCase()?.includes('submitted') &&
      !['C', 'Customer'].includes(orderDetails?.userType)
    );
  }, [orderDetails]);

  const primaryActionBtnText = useMemo(() => {
    if (includePaymentLink && primaryBtnText) {
      return primaryBtnText;
    }
    if (
      (orderDetails?.status === OrderStatusEnum.ORDER_CREATED ||
        orderDetails?.status?.toLowerCase()?.includes('approved') ||
        orderDetails?.status?.toLowerCase()?.includes('submitted')) &&
      !['C', 'Customer'].includes(orderDetails?.userType)
    ) {
      return 'Back To Whatsapp';
    }
    return 'Checkout';
  }, [
    orderDetails?.status,
    orderDetails?.userType,
    includePaymentLink,
    primaryBtnText,
  ]);

  const openDrawer = () => {
    dispatch(updateIsSideDrawerOpen(true));
  };

  const getOrder = () => {
    if (isCart || _.isNull(orderId) || _.isUndefined(orderId)) {
      callCartAPI({
        customerId: customerId ?? inviteId,
        workspaceId: seller?.workspaceId,
        sellerWorkspaceId: seller?.workspaceId,
      });
    } else {
      fetchOrder({
        workspaceId: seller?.workspaceId,
        orderId: Number(orderId),
        body: {
          includeInvoice: true,
          includeTax: true,
          includeCustomer: true,
          includePromotions: true,
          customerId: customerId ?? inviteId,
          includePromotionData: true,
        },
      });
    }
  };

  const getOrderAfterEdit = () => {
    if (isCart) {
      callCartAPI({
        customerId: customerId ?? inviteId,
        workspaceId: seller?.workspaceId,
        sellerWorkspaceId: seller?.workspaceId,
      });
    } else {
      fetchOrderAfterEdit({
        workspaceId: seller?.workspaceId,
        orderId: Number(orderId),
        body: {
          includeInvoice: true,
          includeTax: true,
          includeCustomer: true,
          includePromotions: true,
          customerId: customerId ?? inviteId,
          includePromotionData: true,
        },
      });
    }
  };

  const DUMMY_SCHEMES: OrderLevelScheme[] = [
    {
      id: 1,
      name: 'Buy 10 Get 1 Free',
      code: 'SCH-00344',
      description: 'on purchase of "Armada Bib Cock 2 In 1 With Wall Flange"',
      schemeType: 'BUYXGETY',
      minOrderQuantity: 24,
      schemeIconType: 'FREE',
    },
    {
      id: 2,
      name: 'Flat 2.5% off',
      code: 'SCH-373332',
      description: 'on purchase of "Armada Bib Cock 2 In 1 With Wall Flange"',
      schemeType: 'PRODUCTDISCOUNT',
      minOrderQuantity: 10,
      schemeIconType: 'PERCENT',
    },
    {
      id: 3,
      name: 'Flat 1% off on selected categories.',
      code: 'SCH-128383',
      description:
        'Water Closets, Wash Basins, Faucets, Tiles & Surfaces, Chimneys, Water Heaters.',
      schemeType: 'CATEGORYDISCOUNT',
      minOrderQuantity: 15,
      schemeIconType: 'PRODUCTS',
    },
    {
      id: 4,
      name: 'Flat 1% off on selected division.',
      code: 'SCH-773283',
      description: 'Bathroom, Kitchen,',
      schemeType: 'DIVISIONDISCOUNT',
      minOrderQuantity: 15,
      schemeIconType: 'DIVISION',
    },
  ];

  const onCheckout = () => {
    let poFileIds =
      orderDetails?.poFileIds && orderDetails?.poFileIds?.length !== 0
        ? [...orderDetails?.poFileIds]
        : [orderDetails?.poFileId];
    checkoutAPI({
      sellerWorkspaceId: seller?.workspaceId,
      customerId: customerId ?? inviteId,
      poFileIds: poFileIds,
      ...(shippingAddress && {
        shippingAddress: shippingAddress,
      }),
      ...(checkoutNotes && { notes: checkoutNotes }),
    });
  };

  const onConfirm = () => {
    if (includePaymentLink) {
      if (
        generateRazorpayLinkAPIResponse?.isSuccess &&
        generateRazorpayLinkAPIResponse?.data?.short_url
      ) {
        if (isCart || isDraft) {
          onCheckout();
        }

        window.open(generateRazorpayLinkAPIResponse?.data?.short_url, '_self');
        return;
      } else {
        const data = fetchOrderResponse?.data;
        const orderId = isCart
          ? cartAPIResponse?.data?.files?.[0]?.lines?.find((x) => x.orderId)
              ?.orderId ||
            cartAPIResponse?.data?.files?.[1]?.lines?.find((x) => x.orderId)
              ?.orderId
          : data?.lines?.find((x) => x.orderId)?.orderId;
        if (isCart || isDraft) {
          onCheckout();
        }
        navigate(
          `${WEBROUTES.PAYMENT_ADVICE}/${orderId}?cart=${isCart || isDraft}`
        );
        return;
      }
    }

    if (
      (orderDetails?.status === OrderStatusEnum.ORDER_CREATED ||
        orderDetails?.status?.toLowerCase()?.includes('submitted')) &&
      !['C', 'Customer'].includes(orderDetails?.userType)
    ) {
      onClickBackToWhatsapp({
        whatsappPhoneNo: waConfig?.whatsappPhoneNo,
        workspaceId: seller?.workspaceId,
        isSimulation: isSimulation,
        sendEventToParent: () => {
          sendWAEventToParent({ orderSuccessful: true });
        },
      });
      return;
    }
    if (isSimulation) {
      dispatch(updateCurrentTooltipIndex(3));
    }
    onCheckout();
  };

  const onApplyDiscount = () => {
    const cartOrderId = orderDetails?.lines?.[0]?.orderId;
    if (!cartOrderId || !seller?.workspaceId) {
      setShowToastError({
        show: true,
        msg: 'Unable to fetch eligible benefits. Missing cart/order information.',
      });
      return;
    }

    cartOrderId &&
      getOrderLevelSchemesAPI({
        sellerWorkspaceId: seller?.workspaceId,
        orderId: cartOrderId,
      })
        .unwrap()
        .then((response) => {
          const rawSchemes = Array.isArray(response)
            ? response
            : Array.isArray((response as any)?.schemes)
              ? (response as any)?.schemes
              : Array.isArray((response as any)?.data)
                ? (response as any)?.data
                : Array.isArray((response as any)?.promotions)
                  ? (response as any)?.promotions
                  : [];

          const schemes: OrderLevelScheme[] = rawSchemes.map((scheme: any) => {
            const promotionCode = scheme?.promotionCode || scheme?.code || '';
            const minOrderQuantity =
              scheme?.minQtyASC ??
              scheme?.minOrderQuantity ??
              scheme?.slab?.[0]?.from;

            return {
              id: scheme?.id,
              name: scheme?.name || 'Promotion',
              code: promotionCode,
              description: scheme?.description || '',
              schemeType:
                scheme?.schemeType || scheme?.promotionType || 'PROMOTION',
              minOrderQuantity,
              schemeIconType: scheme?.schemeIconType,
              productVariantId: scheme?.productVariantId,
            };
          });

          setPromotionSchemes(
            schemes && schemes.length > 0 ? schemes : DUMMY_SCHEMES
          );
          setShowPromotionsModal(true);
        })
        .catch(() => {
          setPromotionSchemes(DUMMY_SCHEMES);
          setShowPromotionsModal(true);
        });
  };

  const mapSchemesFromResponse = (response: any): OrderLevelScheme[] => {
    const rawSchemes = Array.isArray(response)
      ? response
      : Array.isArray((response as any)?.schemes)
        ? (response as any)?.schemes
        : Array.isArray((response as any)?.data)
          ? (response as any)?.data
          : Array.isArray((response as any)?.promotions)
            ? (response as any)?.promotions
            : [];

    return rawSchemes.map((scheme: any) => {
      const promotionCode = scheme?.promotionCode || scheme?.code || '';
      const minOrderQuantity =
        scheme?.minQtyASC ??
        scheme?.minOrderQuantity ??
        scheme?.slab?.[0]?.from;

      return {
        id: scheme?.id,
        name: scheme?.name || 'Promotion',
        code: promotionCode,
        description: scheme?.description || '',
        schemeType: scheme?.schemeType || scheme?.promotionType || 'PROMOTION',
        minOrderQuantity,
        schemeIconType: scheme?.schemeIconType,
        productVariantId: scheme?.productVariantId,
      };
    });
  };

  const onApplyPromotion = async (scheme: OrderLevelScheme) => {
    const selectedLine =
      orderDetails?.lines?.find(
        (line) => line?.productVariantId === (scheme as any)?.productVariantId
      ) || orderDetails?.lines?.[0];

    if (
      selectedLine?.orderId &&
      selectedLine?.productVariantId &&
      seller?.workspaceId &&
      (inviteId || customerId)
    ) {
      const lineWithAppliedPromotion = orderDetails?.lines?.find(
        (line) =>
          Array.isArray(line?.promotionIds) && line.promotionIds.length > 0
      );
      const currentPromotionId = lineWithAppliedPromotion?.promotionIds?.[0];

      if (
        currentPromotionId &&
        Number(currentPromotionId) !== Number(scheme.id) &&
        lineWithAppliedPromotion?.orderId
      ) {
        isChangingBenefitRef.current = true;
        try {
          await removeOrderPromotion({
            workspaceId: seller?.workspaceId,
            orderId: Number(lineWithAppliedPromotion.orderId),
            promotionId: Number(currentPromotionId),
          }).unwrap();
        } catch {
          isChangingBenefitRef.current = false;
          setShowToastError({
            show: true,
            msg: 'Unable to change benefit. Please try again.',
          });
          setShowPromotionsModal(false);
          return;
        }
      }

      applyPromotionOnOrder({
        sellerWorkspaceId: seller?.workspaceId,
        customerId: customerId ?? inviteId,
        orderId: String(selectedLine.orderId),
        productVariantId: selectedLine.productVariantId,
        quantity: selectedLine.quantity || 1,
        listPrice: Number(selectedLine.price),
        promotionIds: [String(scheme.id)],
      });
      setSelectedBenefit(scheme);
    }

    setShowPromotionsModal(false);
  };

  const onRemoveBenefit = () => {
    const selectedLine =
      orderDetails?.lines?.find(
        (line) => line?.productVariantId === selectedBenefit?.productVariantId
      ) || orderDetails?.lines?.[0];

    if (
      !selectedLine?.orderId ||
      !selectedLine?.productVariantId ||
      !seller?.workspaceId ||
      !customerId ||
      !inviteId ||
      !selectedBenefit?.id
    ) {
      setShowToastError({
        show: true,
        msg: 'Unable to remove benefit. Missing cart/order information.',
      });
      return;
    }

    removeOrderPromotion({
      workspaceId: seller?.workspaceId,
      orderId: Number(selectedLine.orderId),
      promotionId: Number(selectedBenefit.id),
    });
  };

  const onSkipPromotion = () => {
    setShowPromotionsModal(false);
  };

  const onCancelPromotion = () => {
    setShowPromotionsModal(false);
  };

  const onCancelOrder = () => {
    const orderId = fetchOrderResponse?.data?.lines?.find(
      (x) => x.orderId
    )?.orderId;
    if (!orderId) return;
    cancelOrderAPI({
      workspaceId: seller?.workspaceId,
      sellerWorkspaceId: seller?.workspaceId,
      orderId,
      customerId: customerId ?? inviteId,
    });
  };

  const onAddMore = () => {
    if (fromScreen === 'campaign') {
      navigate(
        `${WEBROUTES.CATALOGUE}?source=cart&type=campaign${
          showPrice ? '' : '&hp=true'
        }`
      );
    } else if (orderId) {
      navigate(
        `${WEBROUTES.CATALOGUE}?source=order&orderId=${orderId}${
          showPrice ? '' : '&hp=true'
        }`
      );
    } else {
      navigate(
        `${WEBROUTES.CATALOGUE}?source=cart${showPrice ? '' : '&hp=true'}`
      );
    }
  };
  const onBackBtnClick = () => {
    navigate(-1);
  };

  const formatShippingAddress = (address?: ShippingAddress): string => {
    const addressParts: string[] = [];

    if (address?.streetLine1?.trim()) {
      addressParts.push(address?.streetLine1.trim());
    }

    if (address?.streetLine2?.trim()) {
      addressParts.push(address?.streetLine2.trim());
    }

    if (address?.city?.trim()) {
      addressParts.push(address?.city.trim());
    }

    if (address?.province?.trim()) {
      addressParts.push(address?.province.trim());
    }

    let result = addressParts.join(', ');

    if (address?.postalCode?.trim()) {
      result += result
        ? ` - ${address?.postalCode.trim()}`
        : address?.postalCode.trim();
    }

    return result;
  };

  useEffect(() => {
    getOrder();
  }, [orderId]);

  useEffect(() => {
    if (fetchOrderResponse?.isSuccess && fetchOrderResponse?.data) {
      if (isEmpty(fetchOrderResponse?.data)) {
        setOrderDetails(undefined);
      } else {
        const data = extractDataFromOrder(fetchOrderResponse?.data);
        setOrderDetails({ ...data });
      }
    }
    if (fetchOrderResponse?.isError) {
      setError({
        show: true,
        msg: (fetchOrderResponse?.error as any)?.data?.message?.message
          ? (fetchOrderResponse?.error as any)?.data?.message?.message
          : 'Something went wrong!',
      });
    }
  }, [fetchOrderResponse]);

  useEffect(() => {
    if (
      fetchOrderAfterEditResponse?.isSuccess &&
      fetchOrderAfterEditResponse?.data
    ) {
      if (isEmpty(fetchOrderAfterEditResponse?.data)) {
        setOrderDetails(undefined);
      } else {
        const data = extractDataFromOrder(fetchOrderAfterEditResponse?.data);
        setOrderDetails(data);
      }
    }
  }, [fetchOrderAfterEditResponse]);

  useEffect(() => {
    if (editOrderResponse?.isLoading) {
      const pvid =
        editOrderResponse?.originalArgs?.body?.lines?.[0]?.productVariantId;
      const orderId = editOrderResponse?.originalArgs?.body?.orderId;
      const isDeleting =
        editOrderResponse?.originalArgs?.body?.lines?.[0]?.quantity === 0;
      if (pvid && orderId) {
        setOrderDetails((prevState) => {
          return {
            ...prevState,
            lines: prevState?.lines?.map((i) => {
              if (
                i?.productVariantId === pvid &&
                i?.orderId === Number(orderId)
              ) {
                return {
                  ...i,
                  ...(isDeleting
                    ? {
                        isDeleting: isDeleting,
                      }
                    : {
                        isLoading: true,
                      }),
                };
              }
              return i;
            }),
          };
        });
      }
    } else {
      setOrderDetails((prevState) => {
        return {
          ...prevState,
          lines: prevState?.lines?.map((i) => {
            return {
              ...i,
              isLoading: false,
              isDeleting: false,
            };
          }),
        };
      });
    }
  }, [editOrderResponse]);

  useEffect(() => {
    if (editOrderResponse?.isSuccess) {
      getOrderAfterEdit();
      if (
        editOrderResponse?.originalArgs?.body?.promotionIds &&
        !_.isEmpty(editOrderResponse?.originalArgs?.body?.promotionIds)
      ) {
        setPromotionApplied(true);
      }
    } else if (editOrderResponse?.isError) {
      if (
        (editOrderResponse?.error as any)?.data?.message?.message ===
        'Quantity cannot be below the Minimum Order Quantity (MOQ)'
      ) {
        const product = orderDetails.lines.find(
          (line) =>
            line.productVariantId ===
            (editOrderResponse?.error as any)?.data?.message?.body?.lines?.[0]
              ?.productVariantId
        );
        setMoqError({
          isError: true,
          quantity: product?.moq || 0,
        });
        setOrderDetails((prevState) => {
          let lines = prevState?.lines?.map((line) => {
            if (
              line?.productVariantId ===
              (editOrderResponse?.error as any)?.data?.message?.body?.lines?.[0]
                ?.productVariantId
            ) {
              return {
                ...line,
                moqError: true,
              };
            }
            return line;
          });
          return {
            ...prevState,
            lines: lines,
          };
        });
      } else {
        setShowToastError({
          show: true,
          msg: 'Unable to apply benefit. Please try again.',
        });
      }
    }
  }, [editOrderResponse]);

  useEffect(() => {
    if (removeOrderPromotionResponse?.isSuccess) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        return;
      }
      setSelectedBenefit(undefined);
      getOrderAfterEdit();
      setShowToastSuccess({
        show: true,
        msg: 'Benefit removed successfully.',
      });
    } else if (removeOrderPromotionResponse?.isError) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        setShowToastError({
          show: true,
          msg: 'Unable to change benefit. Please try again.',
        });
        return;
      }
      setShowToastError({
        show: true,
        msg: 'Unable to remove benefit. Please try again.',
      });
    }
  }, [removeOrderPromotionResponse]);

  useEffect(() => {
    if (checkoutAPIResponse?.isSuccess) {
      let skuCount = orderDetails?.lines?.length;
      if (buyer?.waSessionId) {
        continueChatAPI({
          sessionId: buyer?.waSessionId,
          params: {
            sellerWorkspaceId: seller?.workspaceId,
          },
          body: {
            prefilledVariables: {
              notifyAgentForOrderPlaced: 'true',
              Order: checkoutAPIResponse?.data?.orders || [],
            },
          },
        })
          .unwrap()
          .finally(() =>
            navigate(
              `${WEBROUTES.ORDER_SUCCESSFULL}?skuCount=${skuCount}&successType=order`
            )
          );
      } else {
        navigate(
          `${WEBROUTES.ORDER_SUCCESSFULL}?skuCount=${skuCount}&successType=order`
        );
      }
    }
  }, [checkoutAPIResponse]);

  useEffect(() => {
    const lineWithPromotion = orderDetails?.lines?.find(
      (line) =>
        Array.isArray(line?.promotionIds) && line.promotionIds.length > 0
    );

    // if (!lineWithPromotion) {
    //   setSelectedBenefit(undefined);
    //   return;
    // }

    const promotionId = lineWithPromotion?.promotionIds?.[0];
    const promotionName = lineWithPromotion?.promotionNames?.[0];

    const matchedScheme = promotionSchemes.find(
      (scheme) => Number(scheme.id) === Number(promotionId)
    );

    // setSelectedBenefit({
    //   id: Number(promotionId),
    //   name: matchedScheme?.name || promotionName || 'Applied Benefit',
    //   code: matchedScheme?.code || '',
    //   description: matchedScheme?.description || '',
    //   schemeType: matchedScheme?.schemeType || 'PROMOTION',
    //   minOrderQuantity: matchedScheme?.minOrderQuantity,
    //   schemeIconType: matchedScheme?.schemeIconType,
    //   productVariantId:
    //     matchedScheme?.productVariantId || lineWithPromotion?.productVariantId,
    // });
  }, [orderDetails, promotionSchemes]);

  // Function to clear errors every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (moqError.isError) {
        setMoqError({
          isError: false,
          quantity: 0,
        });
      }
      if (orderDetails?.lines?.some((item) => item?.moqError === true)) {
        setOrderDetails((prevItems) => {
          let lines = prevItems?.lines?.map((line) => {
            return {
              ...line,
              moqError: false,
            };
          });
          return {
            ...prevItems,
            lines: lines,
          };
        });
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [orderDetails, moqError]);

  useEffect(() => {
    getConfigAPI({
      workspaceId: seller?.workspaceId,
      module: ConfigModule.CATALOGUE,
      rollback: false,
    });

    getOrderConfigApi({
      workspaceId: seller?.workspaceId,
      module: ConfigModule.ORDER,
      rollback: false,
    });
  }, []);

  useEffect(() => {
    if (cartAPIResponse?.isSuccess) {
      const data = extractDataFromCart(
        cartAPIResponse?.data?.files,
        cartSummaryAPIResponse?.data
      );

      const isPromotionsApplied =
        cartAPIResponse?.data?.files?.[0]?.lines?.some(
          (x) => x?.promotions?.length > 0
        );
      if (isPromotionsApplied) {
        setPromotionApplied(true);
        const promotion = cartAPIResponse?.data?.files?.[0]?.lines?.find(
          (x) => x?.promotions?.length > 0
        );
        const data = {
          id: promotion?.promotionId,
          name: promotion?.promotionName,
          code: promotion?.priceListCode,
          description: '',
          schemeType: promotion?.schemeType,
          minOrderQuantity: promotion?.MOQ,
          schemeIconType: 'PRODUCTS',
          productVariantId: promotion?.productVariantId,
        };
        //@ts-ignore
        setSelectedBenefit(data);
      }
      setOrderDetails(data);
    }
  }, [cartAPIResponse, cartSummaryAPIResponse]);
  useEffect(() => {
    if (cancelOrderAPIResponse?.isSuccess) {
      onClickBackToWhatsapp({
        whatsappPhoneNo: waConfig?.whatsappPhoneNo,
        workspaceId: seller?.workspaceId,
      });
    }
  }, [cancelOrderAPIResponse]);

  useEffect(() => {
    if (
      (fetchOrderResponse?.isSuccess || cartAPIResponse?.isSuccess) &&
      includePaymentLink &&
      ((isCart && checkoutAPIResponse?.isSuccess) || !isCart)
    ) {
      const data = fetchOrderResponse?.data;
      const orderId = isCart
        ? cartAPIResponse?.data?.files?.[0]?.lines?.find((x) => x.orderId)
            ?.orderId ||
          cartAPIResponse?.data?.files?.[1]?.lines?.find((x) => x.orderId)
            ?.orderId
        : data?.lines?.find((x) => x.orderId)?.orderId;
      const advanceAmount = isCart
        ? cartAPIResponse?.data?.advancePayment
        : data?.advancePaymentAmount;
      const redirectUrl = getBackToWhatsappLink({
        workspaceId: seller?.workspaceId,
        whatsappPhoneNo: waConfig?.whatsappPhoneNo,
      });

      const body = {
        amount: parseFloat(advanceAmount),
        customer: {
          name: buyer?.name,
          contact: buyer?.phone,
          id: customerId ?? inviteId,
        },
        orderId: orderId?.toString(),
        refOrderNumber: isCart
          ? checkoutAPIResponse?.data?.orders?.[0]?.refOrderNumber
          : fetchOrderResponse?.data?.refOrderNumber,
        redirect_url: redirectUrl,
      };
      generateRazorpayLinkAPI({
        params: {
          sellerWorkspaceId: seller?.workspaceId,
        },
        body: body,
      });
    }
  }, [
    includePaymentLink,
    fetchOrderResponse,
    cartAPIResponse,
    checkoutAPIResponse,
  ]);

  useEffect(() => {
    const orderIdToCheck = orderDetails?.lines?.[0]?.orderId;
    if (!showOrderEdit || !orderIdToCheck || !seller?.workspaceId) {
      setPromotionSchemes([]);
      lastPromotionSyncOrderIdRef.current = undefined;
      return;
    }
    if (lastPromotionSyncOrderIdRef.current === Number(orderIdToCheck)) {
      return;
    }
    lastPromotionSyncOrderIdRef.current = Number(orderIdToCheck);

    getOrderLevelSchemesAPI({
      sellerWorkspaceId: seller?.workspaceId,
      orderId: orderIdToCheck,
    })
      .unwrap()
      .then((response) => {
        const schemes = mapSchemesFromResponse(response);
        setPromotionSchemes(schemes);

        const lineWithAppliedPromotion = orderDetails?.lines?.find(
          (line) =>
            Array.isArray(line?.promotionIds) && line.promotionIds.length > 0
        );
        const hasAppliedPromotion = Boolean(lineWithAppliedPromotion);

        // If backend returns no eligible promotions, remove existing applied promotion.
        if (
          schemes.length === 0 &&
          lineWithAppliedPromotion?.orderId &&
          seller?.workspaceId
        ) {
          const promotionIdToRemove =
            lineWithAppliedPromotion?.promotionIds?.[0];
          if (promotionIdToRemove) {
            removeOrderPromotion({
              workspaceId: seller?.workspaceId,
              orderId: Number(lineWithAppliedPromotion.orderId),
              promotionId: Number(promotionIdToRemove),
            });
          }
          return;
        }

        // If promotions exist but nothing is applied yet, auto-apply one.
        // if (!selectedBenefit && !hasAppliedPromotion && schemes.length > 0) {
        //   onApplyPromotion(schemes[0]);
        // }
      })
      .catch(() => {
        setPromotionSchemes([]);
      });
  }, [
    orderDetails?.lines?.[0]?.orderId,
    showOrderEdit,
    seller?.workspaceId,
    getOrderLevelSchemesAPI,
    removeOrderPromotion,
  ]);

  useEffect(() => {
    if (generateRazorpayLinkAPIResponse?.isSuccess) {
      setPrimaryBtnText(
        showAdvancePayment && isDraft
          ? 'Check Out & Pay Advance Amount'
          : showAdvancePayment
            ? 'Pay Advance'
            : isCart
              ? 'Checkout'
              : ''
      );
    } else if (generateRazorpayLinkAPIResponse?.isError) {
      setPrimaryBtnText(
        showAdvancePayment && isDraft
          ? 'Check Out & Pay Advance Amount'
          : showAdvancePayment
            ? 'Pay Advance'
            : isCart
              ? 'Checkout'
              : ''
      );
    }
  }, [generateRazorpayLinkAPIResponse]);

  useEffect(() => {
    if (showToastError?.show) {
      const timeout = setTimeout(() => {
        setShowToastError({
          show: false,
          msg: '',
        });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [showToastError]);
  useEffect(() => {
    if (showToastSuccess?.show) {
      const timeout = setTimeout(() => {
        setShowToastSuccess({
          show: false,
          msg: '',
        });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [showToastSuccess]);

  const ledgerSummary = summaryResponse?.data ?? [];

  const onTnCPress = () => {
    setShowTnCModal(true);
  };

  const onCloseTnCModal = () => {
    setShowTnCModal(false);
  };

  return {
    orderDetails,
    setOrderDetails,
    onChangeQuantity,
    // onValueChange,
    onIncrement,
    onDecrement,
    onDelete,
    onConfirm,
    showImage,
    showPrice,
    showOutOfStock,
    onAddMore,
    isLoading,
    isSubmitting,
    fetchOrderResponse,
    showOrderEdit,
    cartAPIResponse,
    cartSummaryAPIResponse,
    error,
    setError,
    onBackBtnClick,
    moqError,
    hideGoBack,
    primaryActionBtnText,
    selectedSection,
    setSelectedSection,
    formatShippingAddress,
    showCancelBtn,
    onCancelOrder,
    showFooter,
    openDrawer,
    isGettingConfig,
    showPromotionsModal,
    promotionSchemes,
    hasAvailablePromotions,
    selectedBenefit,
    onApplyPromotion,
    onRemoveBenefit,
    onSkipPromotion,
    onCancelPromotion,
    onApplyDiscount,
    showToastError,
    showToastSuccess,
    promotionApplied,
    ledgerSummary,
    isShowOutstandingAmountAndCreditLimit,
    showAdvancePayment,
    onTnCPress,
    showTnCModal,
    onCloseTnCModal,
    advanceAmount,
  };
};
